import { useEffect } from 'react';
import shallow from 'zustand/shallow';

import { LivePriceSymbolsKeyEnum } from 'global/Datafeed/UseLivePrice/liveprice.store.types';
import { useLivePrice } from 'global/Datafeed/UseLivePrice/useLivePrice';
import { OrderbookSymbolsKeyEnum } from 'global/Datafeed/UseOrderbook/orderbook.store.types';
import { useOrderbook } from 'global/Datafeed/UseOrderbook/useOrderbook';
import useOrderbookCompanies from 'global/Securities/stores/useOrderbookCompanies';
import { OrderbookCompaniesResponse } from 'global/Securities/types/api/orderbook';
import { useOrderbookIEPIEV } from 'global/Datafeed/useOrderbookIEPIEV';
import useBestBidOffer from 'global/Datafeed/useBestBidOffer';

const useSecuritiesOrderbook = (
  symbol: string,
  disableOrderbook = false,
): [Partial<OrderbookCompaniesResponse>, boolean] => {
  const [
    orderbookData,
    isLoading,
    loadOrderbookCompanies,
    resetOrderbookCompanies,
    setBidOffer,
    setHeader,
    setIepIev,
    setBestBidOffer,
  ] = useOrderbookCompanies(
    (s) => [
      s.data,
      s.isLoading,
      s.loadOrderbookCompanies,
      s.resetOrderbookCompanies,
      s.setBidOffer,
      s.setHeader,
      s.setIepIev,
      s.setBestBidOffer,
    ],
    shallow,
  );

  useLivePrice(
    [symbol],
    LivePriceSymbolsKeyEnum.SECURITIES_TRANSACTION,
    'securities_transaction',
    setHeader,
  );

  useOrderbook(
    !disableOrderbook ? [symbol] : [],
    OrderbookSymbolsKeyEnum.SECURITIES_TRANSACTION,
    'securities_transaction',
    setBidOffer,
  );

  useOrderbookIEPIEV([symbol], 'securities_transaction', setIepIev);

  useBestBidOffer([symbol], 'securities_transaction', setBestBidOffer);

  const { time_left_seconds: iepIevTimeLeft } = orderbookData?.iepiev ?? {};

  useEffect(() => {
    if (iepIevTimeLeft) {
      const timeoutId = setTimeout(() => {
        setIepIev(null);
      }, iepIevTimeLeft * 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [symbol, iepIevTimeLeft]);

  useEffect(() => {
    if (symbol) loadOrderbookCompanies(symbol);
    else resetOrderbookCompanies();

    return () => {
      resetOrderbookCompanies();
    };
  }, [symbol]);

  return [orderbookData, isLoading];
};

export default useSecuritiesOrderbook;
